<template>
  <div>
    <div class="gsl-hrm-card-container">
      <oxd-form :loading="isLoading" @submit-valid="onSave">
        <div class="gsl-hrm-header-row">
          <oxd-text tag="h6" class="gsl-hrm-main-title"> </oxd-text>
          <oxd-button
            v-if="
              workingDays && projectCurrency && initialBudgetRecords.length > 0
            "
            display-type="secondary"
            :label="$t('general.analyse')"
            @click="onClickBudgetCalculation"
          />
        </div>
        <oxd-divider v-if="!isLoading" />
        <div class="gsl-hrm-timesheet-body">
          <table :class="tableClasses">
            <thead class="gsl-hrm-timesheet-table-header">
              <tr class="gsl-hrm-timesheet-table-header-row">
                <th :class="fixedColumnClasses">
                  <span>{{ $t('general.role') }}</span>
                </th>
                <th :class="fixedColumnClasses">
                  <span>{{ $t('general.head_count') }}</span>
                </th>
                <th :class="fixedColumnClasses">
                  <span>{{ $t('general.rate') }}</span>
                </th>

                <th :class="fixedColumnClasses">
                  <span>{{ $t('general.period') }}</span>
                </th>
                <th
                  v-if="workingDays && projectCurrency"
                  :class="fixedColumnClasses"
                >
                  <span>{{ $t('general.quantity') }}</span>
                </th>
                <th
                  v-if="workingDays && projectCurrency"
                  :class="fixedColumnClasses"
                >
                  <span>{{ $t('general.budgeted_cost') }}</span>
                </th>
              </tr>
            </thead>
            <tbody class="gsl-hrm-timesheet-table-body">
              <tr
                v-for="(record, index) in budgetRecords"
                :key="index"
                class="gsl-hrm-timesheet-table-body-row"
              >
                <td class="gsl-hrm-timesheet-table-body-cell">
                  <job-title-dropdown
                    v-if="isEditing"
                    :rules="rules.role"
                    :model-value="getRole(record)"
                    required
                    @update:model-value="updateRole($event, index)"
                  />
                  <span v-else>
                    {{ record.role ? record.role.name : 'N/A' }}
                  </span>
                </td>
                <td class="gsl-hrm-timesheet-table-body-cell">
                  <oxd-input-field
                    v-if="isEditing"
                    v-model="record.headCount"
                    :rules="rules.headCount"
                    required
                  />
                  <span v-else>
                    {{ record.headCount || 'N/A' }}
                  </span>
                </td>
                <td class="gsl-hrm-timesheet-table-body-cell">
                  <oxd-input-field
                    v-if="isEditing"
                    v-model="record.cost"
                    :rules="rules.cost"
                    required
                  />
                  <span v-else>
                    {{
                      record?.cost?.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }) || 'N/A'
                    }}
                  </span>
                </td>
                <td class="gsl-hrm-timesheet-table-body-cell">
                  <oxd-input-field
                    v-if="isEditing"
                    :rules="rules.period"
                    :model-value="getPeriod(record)"
                    type="select"
                    :options="periodRange"
                    required
                    @update:model-value="updatePeriod($event, index)"
                  />
                  <span v-else>
                    {{
                      record.period
                        ? periodRange.find((item) => item.id === record.period)
                            ?.label
                        : 'N/A'
                    }}
                  </span>
                </td>
                <td
                  v-if="workingDays && projectCurrency"
                  class="gsl-hrm-timesheet-table-body-cell"
                >
                  <span>
                    {{ calculateQuantity(record) || 'N/A' }}
                    {{ calculateQuantity(record) && '(Man-days)' }}
                  </span>
                </td>
                <td
                  v-if="workingDays && projectCurrency"
                  class="gsl-hrm-timesheet-table-body-cell"
                >
                  <span>
                    {{ calculateBudgetedCost(record) && projectCurrency }}
                    {{ calculateBudgetedCost(record) || 'N/A' }}
                  </span>
                </td>
                <td
                  v-if="isEditing"
                  class="gsl-hrm-timesheet-table-body-cell --flex"
                >
                  <oxd-icon-button
                    name="trash"
                    class="gsl-hrm-timesheet-icon"
                    @click="deleteRow(index)"
                  />
                </td>
              </tr>
            </tbody>
          </table>
          <br />
          <div class="gsl-hrm-footer">
            <br />
            <br />
            <oxd-text tag="p" class="gsl-hrm-footer-link-title">
              <b>Quantity:</b> HeadCount x Number of Working days
            </oxd-text>
            <oxd-text tag="p" class="gsl-hrm-footer-link-title">
              <b>Budgeted Cost:</b> Quantity x Cost per day
            </oxd-text>
            <br />
            <oxd-text tag="p" class="gsl-hrm-footer-link-title">
              <b>Note:</b> {{ $t('general.budget_analysis_available_note') }}
            </oxd-text>
          </div>
        </div>
        <oxd-form-actions>
          <oxd-button
            v-if="!isEditing"
            class="gsl-hrm-left-space"
            display-type="secondary"
            :label="$t('general.edit')"
            @click="toggleEditMode"
          />

          <oxd-button
            v-else
            display-type="ghost"
            :label="$t('general.cancel')"
            @click="onCancel"
          />
          <oxd-button
            v-if="isEditing"
            class="gsl-hrm-left-space"
            display-type="secondary"
            :label="$t('time.add_row')"
            @click="addRow"
          />
          <submit-button v-if="isEditing" />
        </oxd-form-actions>
      </oxd-form>
    </div>
    <project-budget-cost-model
      v-if="showBudgetCalculationModal"
      :key="`budget-model-${projectId}-${workingDays}`"
      :data="budgetData()"
      :project-id="projectId"
      @close="onBudgetCalculationModalClose"
    >
    </project-budget-cost-model>
  </div>
</template>
<script>
import {
  required,
  numericOnly,
  validSelection,
} from '@/core/util/validation/rules';
import {APIService} from '@/core/util/services/api.service';
import {onBeforeMount, ref} from 'vue';
import useToast from '@/core/util/composable/useToast';
import {navigate} from '@ohrm/core/util/helper/navigation';
import ProjectBudgetCostModel from '@/orangehrmTimePlugin/components/ProjectBudgetCostModel.vue';
import JobTitleDropdown from '@/orangehrmPimPlugin/components/JobtitleDropdown.vue';
const projectDetails = {
  fromDate: null,
  toDate: null,
};
export default {
  name: 'ProjectBudget',
  components: {
    'job-title-dropdown': JobTitleDropdown,
    'project-budget-cost-model': ProjectBudgetCostModel,
  },
  props: {
    projectId: {
      type: [String, Number],
      required: true,
    },
    workingDays: {
      type: [String, Number],
      required: false,
      default: null,
    },
    projectCurrency: {
      type: [String, null],
      required: true,
      default: null,
    },
    projectDetails: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const {saveSuccess} = useToast();
    const budgetRecords = ref([]);
    const initialBudgetRecords = ref([]);
    const isLoading = ref(true);
    const project = ref({...projectDetails});

    const http = new APIService(
      window.appGlobal.baseUrl,
      `/api/v2/time/project/${props.projectId}/project-budget`,
    );

    const normalizeRecords = (records) => {
      return records.map((record) => {
        return {
          id: record.id,
          role:
            record.role && record.role.id
              ? {id: record.role.id, name: record.role.name}
              : record.roleId
              ? {id: record.roleId, name: record.roleName || 'N/A'}
              : null,
          headCount: String(record?.headCount) ?? null,
          cost:
            record.cost !== null && record.cost !== undefined
              ? String(record.cost) // convert to string
              : '',
          period: record.period ?? null,
        };
      });
    };

    const loadBudget = async () => {
      try {
        const {data} = await http.getAll();

        if (data?.data?.length > 0) {
          budgetRecords.value = normalizeRecords(data.data);
          initialBudgetRecords.value = JSON.parse(JSON.stringify(data.data));
        } else {
          budgetRecords.value = [
            {
              role: null,
              headCount: null,
              cost: null,
              period: null,
            },
          ];
        }
        if (data?.meta?.project.length > 0) {
          project.value.fromDate =
            data.meta.project[0].fromDate?.date?.split(' ')[0] || null;
          project.value.toDate =
            data.meta.project[0].toDate?.date?.split(' ')[0] || null;
        }
      } catch (error) {
        console.error('Error loading budget:', error);
      } finally {
        isLoading.value = false; // Ensure loading state is turned off
      }
    };

    onBeforeMount(() => {
      loadBudget(); // Trigger data loading once before the component is mounted
    });

    return {
      project,
      budgetRecords,
      initialBudgetRecords,
      isLoading,
      http,
      saveSuccess,
      normalizeRecords,
    };
  },
  data() {
    return {
      isEditing: false,
      showBudgetCalculationModal: false,
      periodRange: [
        {id: 'day', label: this.$t('general.per_day')},
        {id: 'week', label: this.$t('general.per_week')},
        {id: 'month', label: this.$t('general.per_month')},
        {id: 'year', label: this.$t('general.per_year')},
      ],
      rules: {
        role: [
          required,
          validSelection,
          (v) => v !== null || this.$t('general.select_a_role'),
          (v) =>
            this.budgetRecords.filter((record) => record.role?.id === v?.id)
              .length < 2 || this.$t('time.duplicate_record'),
        ],
        headCount: [required, numericOnly],
        cost: [required, numericOnly],
        period: [
          required,
          validSelection,
          (v) => v !== null || this.$t('general.select_a_period'),
        ],
      },
    };
  },
  computed: {
    tableClasses() {
      return {
        'gsl-hrm-timesheet-table': true,
        '--editable': this.isEditing,
      };
    },
    fixedColumnClasses() {
      return {
        'gsl-hrm-timesheet-table-header-cell': true,
        '--freeze-left': !this.isEditing,
      };
    },
    fixedCellClasses() {
      return {
        'gsl-hrm-timesheet-table-body-cell': true,
        '--freeze-left': !this.isEditing,
      };
    },
  },
  methods: {
    onBudgetCalculationModalClose() {
      this.showBudgetCalculationModal = false;
    },
    onClickBudgetCalculation() {
      this.showBudgetCalculationModal = true;
    },
    toggleEditMode() {
      this.isEditing = !this.isEditing;
    },
    updateRole($value, index) {
      const updated = this.budgetRecords.map((record, i) => {
        if (i === index) {
          record.role = $value ? $value : null;
        }
        return record;
      });
      return updated;
    },
    updatePeriod($value, index) {
      this.budgetRecords.map((record, i) => {
        if (i === index) {
          record.period = $value ? $value : null;
        }
      });
    },
    getRole(record) {
      const {role} = record;
      if (role && role.label) {
        return role;
      }
      if (role) {
        return {
          id: role.id,
          label: `${role.name}`,
        };
      }
      return null;
    },
    getPeriod(record) {
      const {period} = record;
      if (period && period.label) {
        return period;
      }
      if (period) {
        const matchingPeriod = this.periodRange.find(
          (item) => item.id === period,
        );
        if (matchingPeriod) {
          return {
            id: period,
            label: matchingPeriod.label,
          };
        }
      }
    },
    deleteRow(index) {
      this.budgetRecords.splice(index, 1);
      if (this.budgetRecords.length === 0) this.addRow();
    },
    addRow() {
      this.budgetRecords.push({
        role: null,
        headCount: null,
        cost: null,
        period: null,
      });
    },
    budgetData() {
      return {
        workingDays: this.workingDays,
        projectId: this.projectId,
        currency: this.projectCurrency,
        projectName: this.projectDetails?.projectName,
        fromDate: this.projectDetails.fromDate,
        toDate: this.projectDetails.toDate,
      };
    },
    onSave() {
      this.isEditing = false;
      this.isLoading = true;
      const payload = {
        entries: this.budgetRecords.map((record) => {
          return {
            roleId: record.role.id,
            headCount: record.headCount,
            cost: record.cost,
            period:
              typeof record.period === 'object'
                ? record.period.id
                : record.period,
          };
        }),
        deletedEntries: this.initialBudgetRecords
          .filter((record) => {
            return (
              this.budgetRecords.findIndex(
                (item) => item.role?.id === record.role?.id,
              ) < 0
            );
          })
          .map((record) => ({
            roleId: record?.role?.id,
          })),
      };

      this.http
        .update(this.projectId, payload)
        .then((res) => {
          if (res?.data?.data) {
            const {data} = res.data;
            this.budgetRecords = this.normalizeRecords(data); // Update the budget records with the response data
          }
          return this.saveSuccess();
        })
        .catch(() => {
          return this.saveSuccess();
        })
        .then(() => {
          this.isLoading = false;
        });
    },
    onCancel() {
      navigate(`/time/saveProject/${this.projectId}`);
    },
    calculateWeekdaysBetweenDates(fromDate, toDate) {
      let count = 0;
      let currentDate = new Date(fromDate);

      while (currentDate <= toDate) {
        const dayOfWeek = currentDate.getDay();
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
          // Exclude weekends (0 = Sunday, 6 = Saturday)
          count++;
        }
        currentDate.setDate(currentDate.getDate() + 1);
      }

      return count;
    },
    calculateQuantity(record) {
      if (!this.workingDays) {
        return null;
      }
      const numberOfDays = this.workingDays;
      const quantity = numberOfDays * record.headCount;
      return parseFloat(quantity.toFixed(4));
    },
    calculateBudgetedCost(record) {
      if (!this.workingDays) {
        return null;
      }
      const numberOfDays = this.workingDays;

      // Base rate per day calculation
      let ratePerDay;
      if (record.period === 'day') {
        ratePerDay = record.cost;
      } else if (record.period === 'week') {
        ratePerDay = record.cost / 5; // Divide weekly cost by 5 to get weekday rate
      } else if (record.period === 'month') {
        ratePerDay = record.cost / 21; // Approximate number of weekdays in a month
      } else if (record.period === 'year') {
        ratePerDay = record.cost / 260; // Approximate number of weekdays in a year (52 weeks * 5 days)
      } else {
        return 'N/A'; // Handle invalid periods
      }

      // Calculate the total budgeted cost
      const totalCost = numberOfDays * record.headCount * ratePerDay;
      return totalCost?.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
  },
};
</script>
<style lang="scss" scoped>
.gsl-hrm-footer-link-title {
  font-size: 13px;
  margin-right: 10px;
}
.gsl-hrm-footer-link {
  color: $oxd-primary-one-color;
  font-weight: 600;
  cursor: pointer;
}
.gsl-hrm-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}
</style>
<style src="./timesheet.scss" lang="scss" scoped></style>
